import { Component, OnInit, Input, Output, EventEmitter, QueryList, ViewChildren, OnDestroy } from '@angular/core';

import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ScoutPoint, ScoutPath } from '../../../core/models';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { ScoutPointService, ScoutTaskService } from '../../../core/services';
import { ScoutPointEditorComponent } from '../../scout-point/scout-point-editor/scout-point-editor.component';
import { ScoutPointRefDialogComponent } from '../../scout-point/scout-point-ref-dialog/scout-point-ref-dialog.component';
import { first } from 'rxjs/operators';
import { BehaviorSubject, timer } from 'rxjs';
import { PointDoc } from '../../../core/schema/point-schema';


@Component({
  selector: 'app-scout-segments-editor',
  templateUrl: './scout-segments-editor.component.html',
  styleUrls: ['./scout-segments-editor.component.scss']
})
export class ScoutSegmentsEditorComponent implements OnInit, OnDestroy {

  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() scoutPathId : string
  @Input() buildingPoints : PointDoc[] = []
  @Input() set isLoadingBuildingPoints( bLoading : boolean ) {
    this._isLoadingBuildingPoints = bLoading
    this.buildingPointsSubject.next(bLoading? null : this.buildingPoints)    
  }
  @Input() locked : boolean
  @Output() changed = new EventEmitter<ScoutPath>()
  @Output() pointSelected = new EventEmitter<ScoutPoint>()
  
  @ViewChildren(ScoutPointEditorComponent) pointEditors!: QueryList<ScoutPointEditorComponent>;

  scoutPath : ScoutPath
  pristineScoutPath : ScoutPath
  isEditing = false
  _isLoadingBuildingPoints = false
  get isLoadingBuildingPoints() { return this._isLoadingBuildingPoints }

  buildingPointsSubject = new BehaviorSubject<PointDoc[]>(null)
  bearingDefaultAngles : number[]

  
  constructor( protected scoutTaskService : ScoutTaskService, 
    protected scoutPointService : ScoutPointService,
    private modalService: BsModalService ) { 
  }
  
  ngOnInit(): void {
  }
  
  ngOnDestroy(): void {
  }

  
  externalEditsDlgOpen = false
  configure(){          

    const configureScoutPath = ( scoutPath : ScoutPath ) => {
      this.scoutPath = scoutPath
      this.pristineScoutPath = this.scoutTaskDoc.scoutPaths.find(sp=>sp.id==this.scoutPathId)
      this.bearingDefaultAngles = this.scoutTaskService.getDefaultBearingAngles(this.pristineScoutPath)
      this.updatePoints()
    }

    const newScoutPath = this.scoutTaskDoc.scoutPaths.find(sp=>sp.id==this.scoutPathId)

    if ( !this.isEditing || this.isSaving ) {
      const newScoutPath = this.scoutTaskDoc.scoutPaths.find(sp=>sp.id==this.scoutPathId)
      configureScoutPath(newScoutPath)
    } else if ( !this.externalEditsDlgOpen ) {
      const bPointsDiffer = this.scoutPointService.pointsDiffer(this.pristineScoutPath.scoutPoints,newScoutPath.scoutPoints,true)
      if ( bPointsDiffer ) {
        let initialState = {
          onYes: () => { 
            this.externalEditsDlgOpen = false
            this.cancel(false)           
          },
          onNo: () => {
            this.externalEditsDlgOpen = false
            newScoutPath.scoutPoints = this.scoutPath.scoutPoints
            configureScoutPath(newScoutPath)
          },
          question: 'Scout points have been changed outside editor.  Would you like to accept the external changes and lose your current edits?',
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        }
        this.externalEditsDlgOpen = true
        this.modalService.show(ConfirmModalComponent, { initialState,  id: 1040 });
      } else {
        newScoutPath.scoutPoints = this.scoutPath.scoutPoints
        configureScoutPath(newScoutPath)
      }
    }
  }


  createChild = (parent : ScoutPoint, childOffset : number, pointType : string = 'EX') => {
    const pt = this.scoutTaskService.createScoutPointChild(parent,childOffset,pointType,this.scoutPath,this.buildingPoints)
    this.pathChanged()
    this.setSelectedPoint(pt)    
    this.edit()
    this.enablePointEditor(pt,true)
    return pt
  }


  enablePointEditor(pt:ScoutPoint,bEdit=true) {
    timer(0).subscribe(_=>{ 
      const editor = this.pointEditors.find(editor=>editor.scoutPoint==pt)
      if ( editor ) {
        editor.edit()
      }
    })
  }


  onChildEditorEditModeChanged( editor : ScoutPointEditorComponent ) {
    this.isEditing = this.pointEditors.some(ed=>ed.isEditing)
  }


  onScoutPointChanged( scoutPoint : ScoutPoint ) {
    if ( scoutPoint ) {
        this.pathChanged()
    }
  }


  onScoutPointFormStatusChanged( pointAndStatus : [ScoutPoint,string] ){
    const [scoutPoint, status] = pointAndStatus
    const bValid = status == 'VALID'
    //console.log('status for ' + scoutPoint?.name + ' valid? ' + bValid)
    if ( this.bIsValid != bValid ) {
      this.updateEditingState()
    }
  }


  selectedPoint : ScoutPoint
  setSelectedPoint( scoutPoint : ScoutPoint, bEmitChange = true ) {
    console.log('point selected: ' + scoutPoint?.name)
    const bPointChanged = this.selectedPoint != scoutPoint
    this.selectedPoint = scoutPoint

    if ( bPointChanged && bEmitChange ) {      
      this.pointSelected.emit(scoutPoint)
    }
  }


  setSelectedAndScroll( scoutPoint : ScoutPoint ) {    
    const pointIndex = this.scoutPath.scoutPoints.findIndex(sp=>sp.id==scoutPoint.id)
    const scoutPointElement = document.getElementById('scout-point-editor-' + pointIndex); 
    if (scoutPointElement) {
      scoutPointElement.scrollIntoView({behavior: "smooth", block: "center"});
    }  
    this.setSelectedPoint(scoutPoint)
  }


  edit() {
    this.isEditing = true
    if ( !this.scoutPath.scoutPoints  ) {
      this.scoutPath.scoutPoints = []
    }
    if ( this.scoutPath.scoutPoints.length == 0 ) {
      this.createChild( null, 0, 'HA' )
    }
    this.updateEditingState()
  }


  isSaving = false
  async save() {
    //console.log('saving segments to scoutPath')
    const invalidPointEditors = this.getInvalidPointEditors()
    if ( invalidPointEditors.length ) { 
      this.scoutTaskService.publishError('Invalid measurment input: ' + invalidPointEditors.map(editor=>editor.scoutPoint.name),this,5)     
      return false;
    }
    this.isSaving = true
    try {
        this.updatePoints()
        await this.scoutTaskService.saveScoutPath(this.scoutTaskDoc, this.scoutPath, this.buildingPoints)
        this.isEditing = false
        this.configure()
        this.pathChanged()
    } catch ( error ) {
      this.scoutTaskService.publishError(error,this)
      this.updateEditingState()
    } finally {
      this.isSaving = false;
    }
  }


  async cancel(bPromptIfModified=true) {
    const discardFn = () => {
      this.scoutPath = this.scoutTaskDoc.scoutPaths.find(sp=>sp.id==this.scoutPathId)
      this.pathChanged()
      this.pointEditors.forEach(editor=>editor.cancelEdit())
      this.isEditing=false 
    }
    if ( bPromptIfModified && this.isModified() ) {
      await this.askDiscardEdits(discardFn)
    } else {
      discardFn()
    }
  }

  async askDiscardEdits(acceptDiscardFn: () => void) {
    let initialState = {
      onYes: acceptDiscardFn,
      question: 'Discard scout path changes?',
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.modalService.config.animated = false    
    this.modalService.show(ConfirmModalComponent, { initialState });
  }

  pathChanged = () => {
    //console.log('pathChanged')
    try {
      if ( this.isEditing ) {
        this.updateEditingState()
      }
      this.bearingDefaultAngles = this.scoutTaskService.getDefaultBearingAngles(this.scoutPath)
      this.updatePoints()
      this.pointEditors?.forEach(editor=>editor.updateScoutFromOptions())
      this.changed.emit(this.scoutPath)
    } catch ( error ) {
      this.scoutTaskService.publishError(error,this)
      console.error(error.message)      
    }
  }

  getInvalidPointEditors() {
    return this.pointEditors.filter( (pointEditor : ScoutPointEditorComponent) => !pointEditor.validate() )
  }

  bIsValid = true
  bIsModified = false
  
  updateEditingState() {
    //console.log('updateEditingState')
    this.bIsValid = this.isValid()
    this.bIsModified = this.isModified()
  }

  isValid() {
    const bIsValid = this.getInvalidPointEditors().length == 0
    //console.log('isValid() ' + bIsValid)
    return bIsValid
  }
  
  isModified() {
    const scoutPoints = this.scoutPath?.scoutPoints
    if ( !scoutPoints ) {
      return false
    } 
    return this.scoutPointService.pointsDiffer(scoutPoints,this.pristineScoutPath.scoutPoints,false)
  } 




  
  delete = (scoutPoint : ScoutPoint ) => {
    if ( this.scoutTaskService.removeScoutPoint(scoutPoint,this.scoutPath) ) {
      this.pathChanged()
    }
    this.edit()
  }

  // move up visually in editor
  moveUp = (scoutPoint : ScoutPoint ) => {
    let scoutPoints = this.scoutPath.scoutPoints
    const scoutPointIndex = scoutPoints.findIndex(sp=>sp.id==scoutPoint?.id)
    if ( scoutPointIndex > 0 ) {
      scoutPoints[scoutPointIndex] = scoutPoints[scoutPointIndex-1]
      scoutPoints[scoutPointIndex-1] = scoutPoint
      this.pathChanged()
    }
    this.edit()
  }

  // move down visually in editor
  moveDown = (scoutPoint : ScoutPoint ) => {
    let scoutPoints = this.scoutPath.scoutPoints
    const scoutPointIndex = scoutPoints.findIndex(sp=>sp.id==scoutPoint?.id)
    if ( scoutPointIndex < scoutPoints.length - 1 ) {
      scoutPoints[scoutPointIndex] = scoutPoints[scoutPointIndex+1]
      scoutPoints[scoutPointIndex+1] = scoutPoint
      this.pathChanged()
    }
    this.edit()
  }

  updatePoints( startPt : ScoutPoint = null) {
    
    if ( startPt == null ) {
      if ( this.scoutPath?.scoutPoints?.length ) {
        this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
      }
      return
    } 
  }

  onRefClick = ( scoutPoint : ScoutPoint ) => {

    console.log('show ref data')
    const context : ModalOptions = {
      initialState: {
        scoutPointId: scoutPoint.id,
        scoutPathId: this.scoutPath.id,
        scoutTaskDoc: this.scoutTaskDoc,
        buildingPointsSubject: this.buildingPointsSubject,
        allowEditMode: !this.isEditing, // too hard to merge saved edits from segments editor with modal dlg editor
        locked: this.locked
      },
      class: 'full-screen-modal',
      id: ScoutPointRefDialogComponent.ModalId,
      animated: true,
    } 
    const modalRef = this.modalService.show(ScoutPointRefDialogComponent, context );
    // ngx-bootstrap has onHide but not yet released
    //modalRef.onHide.pipe(first()).subscribe( () => this.pathChanged())
    const scoutModal : ScoutPointRefDialogComponent = modalRef.content
    scoutModal.onClosed.pipe(first()).subscribe( () => {
      if ( scoutModal.bSavedChanges ) {
        if ( !this.isEditing || !this.isModified() ) {
          // okay to just refresh with latest
          this.scoutPath = this.scoutTaskDoc.scoutPaths.find(sp=>sp.id==this.scoutPathId)
        } else {
          console.log('TODO - need to merge latest changes with what is in open editors')
        }
      }
      this.pathChanged()
    });
  }

}
